// ============================================================================
// renderer.js — Three.js scene, renderer, environment and lighting.
// Ships an ashen default look but every environment channel (ground map/tint,
// sky, fog, the three lights, clear colour) can be re-skinned at runtime via
// applyEnv(), so the Conquest layer can drop the same field into snow, forest,
// coast, farmland, highland or marsh without rebuilding geometry.
// No shadow maps (blob-shadow decals handle grounding instead).
// ============================================================================

import * as THREE from 'three';
import {
  FOG_COLOR, FOG_NEAR, FOG_FAR, MAP_SIZE, MAP_HALF,
} from '../core/constants.js';

const GROUND_REP = 46;                 // ground texture tiling across the field
const _texLoader = new THREE.TextureLoader();

export class Renderer {
  constructor(container) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true, powerPreference: 'high-performance', stencil: false,
    });
    this.renderer.setClearColor(FOG_COLOR, 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(FOG_COLOR, FOG_NEAR, FOG_FAR);

    this.camera = new THREE.PerspectiveCamera(58, 1, 0.1, 600);
    this.camera.position.set(0, 8, -12);

    this._gcache = {};   // url -> { map, emis } (repeat-wrapped ground textures)
    this._scache = {};   // url -> sky texture

    this._buildSky();
    this._buildLights();
    this._buildGround();

    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  // ---- texture caches -------------------------------------------------------
  _ground(url) {
    if (this._gcache[url]) return this._gcache[url];
    const mk = (srgb) => {
      const t = _texLoader.load(url);
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(GROUND_REP, GROUND_REP);
      t.anisotropy = 8;
      if (srgb) t.colorSpace = THREE.SRGBColorSpace;
      return t;
    };
    return (this._gcache[url] = { map: mk(true), emis: mk(false) });
  }
  _skyTex(url) {
    if (this._scache[url]) return this._scache[url];
    const t = _texLoader.load(url);
    t.colorSpace = THREE.SRGBColorSpace;
    t.wrapS = THREE.RepeatWrapping;
    return (this._scache[url] = t);
  }

  _buildSky() {
    const geo = new THREE.SphereGeometry(400, 48, 32);
    this.skyMat = new THREE.MeshBasicMaterial({
      map: this._skyTex('art/sky.png'), side: THREE.BackSide, depthWrite: false, fog: false,
    });
    this.sky = new THREE.Mesh(geo, this.skyMat);
    this.scene.add(this.sky);
  }

  _buildLights() {
    this.sun = new THREE.DirectionalLight(0xffa05a, 1.2);
    this.sun.position.set(-60, 80, -40);
    this.scene.add(this.sun);
    this.hemi = new THREE.HemisphereLight(0x3a4658, 0x2a1c14, 0.95);
    this.scene.add(this.hemi);
    this.amb = new THREE.AmbientLight(0x3a2418, 0.5);
    this.scene.add(this.amb);
  }

  _buildGround() {
    const g = this._ground('art/ground.png');
    this.groundMat = new THREE.MeshLambertMaterial({
      color: 0xdedede, map: g.map,
      emissive: 0xff5210, emissiveIntensity: 0.42, emissiveMap: g.emis,
    });
    const geo = new THREE.PlaneGeometry(MAP_SIZE, MAP_SIZE, 1, 1);
    geo.rotateX(-Math.PI / 2);
    this.ground = new THREE.Mesh(geo, this.groundMat);
    this.ground.renderOrder = -1;
    this.scene.add(this.ground);

    // border ridge — recoloured with the ground so the field edge always reads
    this.ringMat = new THREE.MeshLambertMaterial({ color: 0x120d0a, emissive: 0x4a1e08, emissiveIntensity: 0.6 });
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(MAP_HALF - 6, MAP_HALF, 64).rotateX(-Math.PI / 2), this.ringMat
    );
    ring.position.y = 0.02;
    this.scene.add(ring);
  }

  // Re-skin the whole environment for a biome. `e` is a plain config (see biomes.js).
  applyEnv(e) {
    const g = this._ground(e.ground);
    this.groundMat.map = g.map;
    this.groundMat.emissiveMap = g.emis;
    this.groundMat.color.setHex(e.groundTint);
    this.groundMat.emissive.setHex(e.groundEmissive);
    this.groundMat.emissiveIntensity = e.groundEmisInt;
    this.groundMat.needsUpdate = true;

    this.skyMat.map = this._skyTex(e.sky);
    this.skyMat.needsUpdate = true;

    this.scene.fog.color.setHex(e.fog);
    this.scene.fog.near = e.fogNear;
    this.scene.fog.far = e.fogFar;
    this.renderer.setClearColor(e.fog, 1);

    this.sun.color.setHex(e.sun); this.sun.intensity = e.sunInt;
    this.hemi.color.setHex(e.hemiSky); this.hemi.groundColor.setHex(e.hemiGround); this.hemi.intensity = e.hemiInt;
    this.amb.color.setHex(e.amb); this.amb.intensity = e.ambInt;

    this.ringMat.color.setHex(e.groundTint);
    this.ringMat.emissive.setHex(e.groundEmissive);
    this.ringMat.emissiveIntensity = Math.min(0.6, e.groundEmisInt + 0.1);
  }

  add(obj) { this.scene.add(obj); }

  resize() {
    const w = window.innerWidth, h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  render() {
    if (this.sky) this.sky.position.copy(this.camera.position);
    this.renderer.render(this.scene, this.camera);
  }

  get drawCalls() { return this.renderer.info.render.calls; }
  get triangles() { return this.renderer.info.render.triangles; }
}
